import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LSSDChatbot from "@/components/LSSDChatbot";
import GlobalRevealObserver from "@/components/GlobalRevealObserver";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import Home from "@/pages/Home";
import Recruitment from "@/pages/Recruitment";
import Arruolamento from "@/pages/Arruolamento";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NewsDetail from "@/pages/NewsDetail";
import CodicePenalePublic from "@/pages/CodicePenalePublic";
import CandidatureStatus from "@/pages/CandidatureStatus";
import Storia from "@/pages/Storia";
import Divisioni from "@/pages/Divisioni";
import Divisione from "@/pages/Divisione";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import AreaAgenti from "@/pages/AreaAgenti";
import AreaHR from "@/pages/AreaHR";
import AreaComando from "@/pages/AreaComando";
import AreaEsecutiva from "@/pages/AreaEsecutiva";
import AreaSceriffo from "@/pages/AreaSceriffo";
import AreaProfilo from "@/pages/AreaProfilo";
import AreaAllievi from "@/pages/AreaAllievi";
import AreaManuale from "@/pages/AreaManuale";
import AreaDetectiveBureau from "@/pages/AreaDetectiveBureau";
import OperatorProfilePublic from "@/pages/OperatorProfilePublic";
import Roster from "@/pages/Roster";
import AuthCallback from "@/pages/AuthCallback";
import CompletaProfilo from "@/pages/CompletaProfilo";
import RegistrazioneInAttesa from "@/pages/RegistrazioneInAttesa";
import "@/App.css";

/**
 * OAuthCallbackGate — intercept #session_id in URL fragment BEFORE routes render.
 * Uses useLocation().hash (reactive) as required by the Emergent Auth playbook.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function OAuthCallbackGate({ children }) {
  const { hash } = useLocation();
  if (hash && hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function PublicLayout() {
  const { pathname } = useLocation();
  const hideFooter = pathname.startsWith("/dashboard");
  // Sul Codice Penale è già presente `PenalCodeChatbot` (specialistico sul codice):
  // evitiamo di sovrapporre due chatbot flottanti.
  const hideGlobalChat = pathname.startsWith("/codice-penale");
  return (
    <div className="public-shell" data-testid="public-shell">
      <GlobalRevealObserver />
      <Navbar />
      <PageTransition variant="soft" />
      {!hideFooter && <Footer />}
      {!hideGlobalChat && <LSSDChatbot />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster richColors position="top-right" />
        <OAuthCallbackGate>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/storia" element={<Storia />} />
            <Route path="/divisioni" element={<Divisioni />} />
            <Route path="/divisioni/:slug" element={<Divisione />} />
            <Route path="/codice-penale" element={<CodicePenalePublic />} />
            <Route path="/news/:id" element={<NewsDetail />} />
            <Route path="/arruolamento" element={<Arruolamento />} />
            <Route path="/reclutamenti" element={<Recruitment />} />
            <Route path="/candidatura" element={<CandidatureStatus />} />
            <Route path="/candidatura/:code" element={<CandidatureStatus />} />
            <Route path="/operatore/:slug" element={<OperatorProfilePublic />} />
            <Route path="/roster" element={<Roster />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/completa-profilo" element={<CompletaProfilo />} />
          <Route path="/registrazione-in-attesa" element={<RegistrazioneInAttesa />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route
              path="agenti"
              element={
                <ProtectedRoute requireAgents>
                  <AreaAgenti />
                </ProtectedRoute>
              }
            />
            <Route
              path="hr"
              element={
                <ProtectedRoute requireHR>
                  <AreaHR />
                </ProtectedRoute>
              }
            />
            <Route
              path="detective-bureau"
              element={
                <ProtectedRoute requireDetectiveBureau>
                  <AreaDetectiveBureau />
                </ProtectedRoute>
              }
            />
            <Route
              path="comando"
              element={
                <ProtectedRoute requireCommand>
                  <AreaComando />
                </ProtectedRoute>
              }
            />
            <Route
              path="esecutiva"
              element={
                <ProtectedRoute requireExecutive>
                  <AreaEsecutiva />
                </ProtectedRoute>
              }
            />
            <Route
              path="sceriffo"
              element={
                <ProtectedRoute requireSheriff>
                  <AreaSceriffo />
                </ProtectedRoute>
              }
            />
            <Route
              path="profilo"
              element={
                <ProtectedRoute>
                  <AreaProfilo />
                </ProtectedRoute>
              }
            />
            <Route
              path="allievi"
              element={
                <ProtectedRoute requireStudent>
                  <AreaAllievi />
                </ProtectedRoute>
              }
            />
            <Route
              path="manuale"
              element={
                <ProtectedRoute>
                  <AreaManuale />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
        </OAuthCallbackGate>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
